import { Injectable } from '@angular/core';
import { EPlatformFeature } from '@enums/campaign.enum';
import { IMatchedKpiAnalyticsApi, TKpiAnalyticsKey } from '@interfaces/campaign-analytics-api.interface';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { IKpiAggregateValues, IPlatformFeatureKpis } from './campaign-details-kpis.interface';

const KPI_ANALYTICS_KEYS: TKpiAnalyticsKey[] = [
  'avgViewers',
  'sponsoredAvgViewers',
  'comments',
  'engagementRate',
  'engagementRateViews',
  'engagements',
  'impressions',
  'likes',
  // 'mediaValue',
  'peakViewers',
  'sponsoredPeakViewers',
  'predictedImpressions',
  'quotes',
  'reach',
  'retweets',
  'shares',
  'totalWatchedHours',
  'sponsoredTotalWatchedHours',
  'views',
];

@Injectable()
export class CampaignDetailsKpisService {
  public buildPlatformFeatureKpis(
    matchedKpis: IMatchedKpiAnalyticsApi[],
    selectedPlatformFeatures: Set<EPlatformFeature>,
  ): IPlatformFeatureKpis[] {
    const matchedKpisByPlatform: Map<EPlatformFeature, IMatchedKpiAnalyticsApi[]> = new Map();

    for (const matchedKpi of matchedKpis) {
      if (selectedPlatformFeatures.size > 0 && !selectedPlatformFeatures.has(matchedKpi.platformFeature)) {
        continue;
      }

      const platformFeatureKpis: IMatchedKpiAnalyticsApi[] =
        matchedKpisByPlatform.get(matchedKpi.platformFeature) ?? [];
      platformFeatureKpis.push(matchedKpi);
      matchedKpisByPlatform.set(matchedKpi.platformFeature, platformFeatureKpis);
    }

    return Array.from(matchedKpisByPlatform.entries()).map(([platformFeature, platformMatchedKpis]) => ({
      kpis: this.buildKpiAggregates(platformMatchedKpis),
      platformColor: this.getPlatformColor(platformFeature),
      platformFeature,
    }));
  }

  private buildKpiAggregates(
    matchedKpis: IMatchedKpiAnalyticsApi[],
  ): Partial<Record<TKpiAnalyticsKey, IKpiAggregateValues>> {
    const aggregates: Partial<Record<TKpiAnalyticsKey, IKpiAggregateValues>> = {};

    for (const analyticsKey of KPI_ANALYTICS_KEYS) {
      const avgValues: number[] = [];
      const maxValues: number[] = [];
      const totalValues: number[] = [];

      for (const matchedKpi of matchedKpis) {
        if (analyticsKey === 'mediaValue') {
          const val: number | null = this.getNumericValue(matchedKpi.matchedMediaValue.mediaValue);

          if (val !== null) {
            avgValues.push(val);
            maxValues.push(val);
            totalValues.push(val);
          }
        } else {
          const avgVal: number | null = this.getNumericValue(matchedKpi.matchedAvgKpiAnalytics[analyticsKey]);
          const maxVal: number | null = this.getNumericValue(matchedKpi.matchedMaxKpiAnalytics[analyticsKey]);
          const totalVal: number | null = this.getNumericValue(matchedKpi.matchedTotalKpiAnalytics[analyticsKey]);

          if (avgVal !== null) {
            avgValues.push(avgVal);
          }

          if (maxVal !== null) {
            maxValues.push(maxVal);
          }

          if (totalVal !== null) {
            totalValues.push(totalVal);
          }
        }
      }

      if (avgValues.length === 0 && maxValues.length === 0 && totalValues.length === 0) {
        continue;
      }

      aggregates[analyticsKey] = {
        avgValue: avgValues.length > 0 ? avgValues.reduce((sum, v) => sum + v, 0) / avgValues.length : null,
        maxValue: maxValues.length > 0 ? Math.max(...maxValues) : null,
        totalValue: totalValues.length > 0 ? totalValues.reduce((sum, v) => sum + v, 0) : null,
      };
    }

    return aggregates;
  }

  private getNumericValue(value: number | null | undefined): number | null {
    if (value === null || value === undefined || typeof value !== 'number') {
      return null;
    }

    return value;
  }

  private getPlatformColor(platformFeature: EPlatformFeature): string {
    return SocialUtilitiesService.platformColorMap[SocialUtilitiesService.getPlatform(platformFeature)];
  }
}
